import { FormikValues } from 'formik';
import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';

import { Field } from './Field';
import { FieldError } from '.';

export type FieldValidation<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> = FieldValidateEntry<TValue, TValues>[];

export interface FieldValidateEntry<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> {
  type: FieldValidationTypes;
  message?: string;
  fieldLength?: number;
  regExp?: RegExp;
  validate?: (field: Field<TValue, TValues>, value: TValue, values: TValues) => FieldError;
}

export enum FieldValidationTypes {
  Required = 'REQUIRED',
  Custom = 'CUSTOM',
}

export type FieldErrors = {
  [key: string]: FieldError; // define key type
};

type BuilderType<TValue extends any = any, TValues extends FormikValues = FormikValues> = {
  required: (message: string) => BuilderType<TValue, TValues>;
  url: (message: string) => BuilderType<TValue, TValues>;
  email: (message: string) => BuilderType<TValue, TValues>;
  errorIf: (
    message: string,
    condition: (field: Field<TValue, TValues>, value: TValue, values: TValues) => boolean,
  ) => BuilderType<TValue, TValues>;
  custom: (
    validate: (field: Field<TValue, TValues>, value: TValue, values: TValues) => FieldError,
  ) => BuilderType<TValue, TValues>;
  build: () => FieldValidation<TValue, TValues>;
};

export function validate<TValue extends any = any, TValues extends FormikValues = FormikValues>() {
  const validateFields: FieldValidation<TValue, TValues> = [];

  const builder: BuilderType<TValue, TValues> = {
    required: (message) => {
      validateFields.push({
        type: FieldValidationTypes.Required,
        message,
      });
      return builder;
    },
    url: (message) => {
      validateFields.push({
        type: FieldValidationTypes.Custom,
        validate: (field, value) => {
          return !isURL(value as string) ? message : undefined;
        },
      });
      return builder;
    },
    email: (message) => {
      validateFields.push({
        type: FieldValidationTypes.Custom,
        validate: (field, value) => {
          return !isEmail(value as string) ? message : undefined;
        },
      });
      return builder;
    },
    errorIf: (message, condition) => {
      validateFields.push({
        type: FieldValidationTypes.Custom,
        validate: (field, value, values) => {
          return condition(field, value, values) ? message : undefined;
        },
      });
      return builder;
    },
    custom: (validate) => {
      validateFields.push({
        type: FieldValidationTypes.Custom,
        validate,
      });
      return builder;
    },
    build: () => validateFields,
  };

  return builder;
}

export function required<TValue extends any = any, TValues extends FormikValues = FormikValues>(
  message: string,
): FieldValidation<TValue, TValues> {
  return validate<TValue, TValues>().required(message).build();
}
